import PropTypes from 'prop-types'
import React from 'react'
import AuthorLayout from 'src/components/pages/AuthorLayout'
import Layout from '../../src/components/layouts'
import { generateSeoData } from '../../src/common/nextSeoUtils'
import { getFullUrlFromReq } from '../../src/common/serverUtils'
import SearchService from '../../src/services/SearchService'
import logger from '../../src/common/logger'
import { jsonLdData } from '../../src/common/constants/jsonLd'
import CustomJsonLd from '../../src/components/shared/CustomJsonLd'
import WithSeoOverrides from 'src/components/shared/WithSeoOverride'

export async function getServerSideProps({
  params: { pid = [] },
  req,
  preview,
}) {
  let author = {}
  let authorPosts = []
  const [authorSlug, prefix, page] = pid
  if (
    !authorSlug ||
    (typeof prefix !== 'undefined' && prefix !== 'page') ||
    (typeof page !== 'undefined' && Number.isNaN(Number(page))) ||
    page < 1
  ) {
    return { notFound: true }
  }

  const urlPrefix = `/author/${authorSlug}/`

  const currentPage = Number(page) || 1
  if (currentPage === 1 && prefix) {
    return {
      redirect: {
        destination: urlPrefix,
      },
    }
  }

  try {
    const {
      author: {
        items: [authorData],
      },
    } = await SearchService.getInstance().getAuthorPostsCollection(
      authorSlug,
      { page: currentPage },
      preview
    )

    if (!authorData) {
      return { notFound: true }
    }

    const {
      linkedFrom: {
        posts: { total, items: _posts = [] },
      },
      ..._author
    } = authorData

    const posts = _posts.filter((item) => {
      return (
        item?.linkedFrom?.page.items.length &&
        item?.linkedFrom?.page.items[0] !== null
      )
    })

    authorPosts = {
      total,
      currentPage,
      items: posts.map((item) => {
        return {
          ...item,
          updatedAt: item.sys.publishedAt.split('.').shift(),
          slug: item?.linkedFrom?.page.items[0].path,
          imageAsset: item.image,
        }
      }),
    }

    author = _author
  } catch (error) {
    logger.error(`Author page: failed to get serverside props`, {
      authorSlug,
      error,
    })

    return { notFound: true }
  }

  const pageCount = Math.ceil(authorPosts.total / 10) || 1
  const pagePid = ['author'].concat(pid)
  const { canonicalUrl } = getFullUrlFromReq(req, pagePid)
  const pageTitle = author.metaTitle ? author.metaTitle : author.fullName
  const seoData = generateSeoData({
    title: `${
      currentPage === 1
        ? pageTitle
        : `${pageTitle} | Page ${currentPage} of ${pageCount}`
    }`,
    description: author.metaDescription,
    url: canonicalUrl,
    lastUpdate: author.updatedAt,
    images: [
      {
        url: author.avatar,
      },
    ],
  })

  return {
    props: {
      author,
      seoData,
      authorPosts,
      urlPrefix,
      currentPage,
    },
  }
}

const AuthorPage = ({
  author,
  seoData,
  authorPosts,
  urlPrefix,
  currentPage,
}) => (
  <Layout footerClassName="max-w-1400">
    <WithSeoOverrides {...seoData} />
    <CustomJsonLd
      id={'webPage'}
      data={jsonLdData(
        seoData?.title,
        seoData?.description,
        currentPage === 1 ? urlPrefix : `${urlPrefix}${currentPage}`
      )}
    />
    <AuthorLayout author={author} authorPosts={authorPosts} />
  </Layout>
)

AuthorPage.defaultProps = {
  author: {
    avatar: '',
  },
  authorPosts: {},
}
AuthorPage.propTypes = {
  author: PropTypes.object,
  seoData: PropTypes.object.isRequired,
  authorPosts: PropTypes.object,
  urlPrefix: PropTypes.string,
  currentPage: PropTypes.number,
}

export default AuthorPage
